import type { AnyAction } from '@reduxjs/toolkit';
import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import cloneDeep from 'lodash/cloneDeep';

import { startResult } from '@webapp/account/src/resources/reports/async';
import { addStateFetch } from '@webapp/account/src/resources/store-utils';
import { initialAddons } from '@webapp/account/src/resources/survey/addons/addons';

import type { ChartGrouping } from '@webapp/common/lib/date';
import { ListPos } from '@webapp/common/lib/ui';
import { getOrderedMoveAndSort, orderedMoveAndSort, updateItemInArray } from '@webapp/common/lib/utils';

import {
    addonSave,
    addPromocodes,
    addShareLink,
    addSurvey,
    answersRemove,
    changeShareLink,
    copySurvey,
    deleteShareLink,
    deleteUserFiles,
    folderAdd,
    folderListGet,
    folderMove,
    folderRemove,
    folderSave,
    getFileToken,
    getPersonalPasswords,
    getShareLinks,
    getStat,
    getSurvey,
    getSurveyList,
    getSurveyPromocodes,
    licenseApply,
    moveToFolder,
    refreshSurvey,
    removeSurvey,
    saveSurvey,
    sortSurvey,
    subdomainChange,
} from './async';
import { type Chart, type ChartData, initialChartData } from './chart';
import type { Folder } from './folder';
import { initialFolder } from './folder';
import { findFolder, findRelativePos, FOLDER_SHOW_COUNT_INC, mapShareLink, redirect, TMP_NEW_FOLDER_ID } from './lib';
import type { SurveyState } from './state';
import { initialState } from './state';
import type { Survey } from './survey';
import { initialSurvey, SurveyStatus } from './survey';

const listFetch = (s: SurveyState): void => {
    s.fetchItems = true;
};

const listFetchEnd = (s: SurveyState): void => {
    s.fetchItems = false;
};

const folderFetch = (s: SurveyState): void => {
    s.fetchFolders = true;
};

const folderFetchEnd = (s: SurveyState): void => {
    s.fetchFolders = false;
};

const fetch = (state: SurveyState, { surveyId }: { surveyId: number }): void => {
    updateItemInArray(state.items, surveyId, (r) => {
        r.fetch = true;
    });
};

const fetchEnd = ({ items }: SurveyState, { surveyId }: { surveyId: number }): void => {
    updateItemInArray(items, surveyId, (r: Survey) => {
        r.fetch = false;
    });
};

const fail = (s: SurveyState, { surveyId }: { surveyId: number }): void => {
    s.saveParams = false;
    fetchEnd(s, { surveyId });
};

const insertOrderedItem = <T extends { order: number }>(arr: Array<T>, at: number, item: T): void => {
    arr.splice(at + 1, 0, item);
    arr.forEach((c, idx) => {
        c.order = idx + 1;
    });
    arr.sort((a, b) => a.order - b.order);
};

const saveFetch = (
    s: SurveyState,
    { key, surveyId, value }: { key: keyof Survey; surveyId: number; value: never },
): void => {
    s.saveParams = false;
    updateItemInArray(s.items, surveyId, (s: Survey) => {
        s[key] = value;
        s.enabled = s.status === SurveyStatus.INWORK; // TODO move to selector
    });
};

const countFolders = (s: SurveyState): void => {
    s.countWithoutFolder = s.items.reduce((cnt, i) => cnt + Number(i.folderId === null), 0);
    s.folders.forEach((folder) => {
        folder.count = s.items.reduce((cnt, i) => cnt + Number(i.folderId === folder.id), 0);
        folder.children.forEach((childFolder) => {
            childFolder.count = s.items.reduce((cnt, i) => cnt + Number(i.folderId === childFolder.id), 0);
            folder.count += childFolder.count;
        });
    });
};

const startPublicSuccess = (
    s: SurveyState,
    {
        payload: {
            createdDatetime,
            endDatetime,
            main,
            params,
            questionTypes,
            share,
            surveyId,
            tests,
            passwordAccess,
            defaultLang,
            multiLang,
        },
    }: AnyAction,
): void => {
    if (passwordAccess) return;

    s.activeSurveyId = surveyId;
    s.items = [
        {
            ...cloneDeep(initialSurvey),
            defaultLang,
            multiLang,
            questionTypes: new Set(questionTypes),
            id: surveyId,
            createdDatetime: createdDatetime,
            endDatetime: endDatetime,
            addons: {
                ...cloneDeep(initialAddons),
                share,
                tests,
                main,
                params,
            },
        },
    ];
};

export const surveySlice = createSlice({
    name: 'survey',
    initialState,
    reducers: {
        setQuestionTypes({ activeSurveyId, items }, { payload: { questionTypes } }) {
            updateItemInArray(items, activeSurveyId, (s: Survey) => {
                s.questionTypes = questionTypes;
            });
        },
        setChartGrouping(s, { payload: { chartGrouping } }: { payload: { chartGrouping: ChartGrouping } }) {
            s.chartGrouping = chartGrouping;
        },
        setSearch(s, { payload: { search } }: { payload: { search: string } }) {
            s.search = search;
            s.paginationPage = 1;
        },
        setPaginationPage(s, { payload: { page } }: { payload: { page: number } }) {
            s.paginationPage = page;
        },
        folderSelect(s, { payload: { folderId } }) {
            const isRoot = folderId === null;
            const folder = findFolder(s.folders, folderId);

            if (!isRoot && folder) {
                s.expandedFolderId = folder.parent === null ? folderId : folder.parent;
            } else {
                s.expandedFolderId = null;
            }

            s.items.forEach((i) => {
                i.hidden = isRoot ? !!i.folderId : i.folderId !== folderId;
            });
        },
    },
    extraReducers: (builder) =>
        addStateFetch(
            builder
                .addCase(getFileToken.fulfilled, (s, { payload: { link } }) => {
                    s.fileLink = link;
                    redirect(link);
                })
                .addCase(getSurveyList.fulfilled, (s, { payload: { items } }) => {
                    listFetchEnd(s);
                    s.fetchItems = false;
                    s.items = items.map((s: Survey) => ({
                        ...s,
                        hidden: true,
                    }));
                    countFolders(s);
                })
                .addCase(sortSurvey.pending, ({ items }, { meta: { arg } }) => {
                    const { overId, surveyId } = arg;
                    const pos = findRelativePos(items, surveyId, overId);
                    arg.pos = pos;
                    if (pos !== ListPos.SAME) {
                        orderedMoveAndSort(items, surveyId, overId);
                    }
                })
                .addCase(subdomainChange.fulfilled, ({ items }, { meta: { arg } }) => {
                    const { subdomain, surveyId } = arg;
                    updateItemInArray(items, surveyId, (s: Survey) => {
                        s.subdomain = subdomain;
                        s.subdomainError = false;
                    });
                })
                .addCase(getStat.pending, (s, { meta: { arg } }) => {
                    const {
                        filter: { chartGrouping },
                    } = arg;
                    s.chartGrouping = chartGrouping;
                    s.fetchStat = true;
                })
                .addCase(getStat.rejected, (s) => {
                    s.fetchStat = false;
                })
                .addCase(
                    getStat.fulfilled,
                    (
                        s,
                        { payload: { chartData, charts } }: { payload: { chartData: ChartData; charts: Array<Chart> } },
                    ) => {
                        s.chartData = chartData;
                        s.charts = charts.map((chart) => {
                            let total = 0;

                            for (const { count } of chart.legends) {
                                total += count;
                            }

                            chart.legends.forEach((l) => {
                                l.percent = Math.round(100 / (total / l.count));
                            });

                            return chart;
                        });
                        s.fetchStat = false;
                    },
                )
                .addCase(folderSave.fulfilled, (s, { payload: { folderId, name } }) => {
                    folderFetchEnd(s);
                    const folder = findFolder(s.folders, folderId);
                    folder.name = name;
                    folder.edit = false;
                })
                .addCase(folderRemove.fulfilled, (s, { payload: { folderId } }) => {
                    folderFetchEnd(s);
                    const folder = findFolder(s.folders, folderId);
                    if (!folder) return;
                    if (folder.parent === null) {
                        const at = s.folders.findIndex(({ id }) => id === folder.id);
                        s.folders.splice(at, 1);
                    } else if (folder.parent) {
                        const { children } = findFolder(s.folders, folder.parent);
                        const at = children.findIndex(({ id }) => id === folder.id);
                        children.splice(at, 1);
                    }
                })
                .addCase(folderListGet.fulfilled, (s, { payload: { folders } }) => {
                    folderFetchEnd(s);
                    s.folders = folders;
                    s.foldersLoaded = true;
                    s.visibleCount = Math.min(folders.length, FOLDER_SHOW_COUNT_INC);
                    s.folders.forEach((f) => {
                        f.visibleCount = Math.min(f.children.length, FOLDER_SHOW_COUNT_INC);
                    });
                    countFolders(s);
                })
                .addCase(deleteUserFiles.pending, (s) => {
                    s.deleteFiles = true;
                })
                .addCase(removeSurvey.fulfilled, (s, { payload: { surveyId } }) => {
                    s.items = s.items.filter((item) => item.id !== surveyId);
                    countFolders(s);
                })
                .addCase(copySurvey.fulfilled, ({ items }) => {
                    items.forEach((i) => {
                        i.fetch = false;
                        i.expandSettings = false;
                    });
                })
                .addCase(licenseApply.fulfilled, ({ items }, { payload: { surveyId } }) => {
                    updateItemInArray(items, surveyId, (s: Survey) => {
                        s.status = SurveyStatus.INWORK;
                        s.enabled = true; // TODO move to selector
                        s.isPro = true;
                        s.fetch = false;
                    });
                })
                .addCase(addonSave.pending, (s, { meta: { arg } }) => {
                    const { key, name, value } = arg;
                    s.saveParams = true;
                    updateItemInArray(s.items, s.activeSurveyId, (s: Survey) => {
                        if (key) {
                            s.addons[name][key] = value;
                        } else {
                            s.addons[name] = { ...s.addons[name], ...value };
                        }
                    });
                })

                .addCase(getSurvey.fulfilled, (s, { payload: { survey, surveyId } }) => {
                    const exists = s.items.some(({ id }) => surveyId === id);

                    if (exists) {
                        updateItemInArray(s.items, survey.id, (s) => {
                            Object.assign(s, survey);
                        });
                    } else {
                        s.items.push({
                            ...cloneDeep(initialSurvey),
                            ...survey,
                        });
                    }
                    s.activeSurveyId = surveyId;
                    listFetchEnd(s);
                })
                .addCase(refreshSurvey.fulfilled, (s, { payload: { survey, surveyId } }) => {
                    updateItemInArray(s.items, surveyId, (s) => {
                        Object.assign(s, survey);
                    });
                })
                .addCase(folderAdd.pending, (s, { meta: { arg } }) => {
                    const { after, name, parentId } = arg;
                    folderFetch(s);
                    const tmpFolder: Folder = {
                        ...cloneDeep(initialFolder),
                        visibleCount: 0,
                        id: TMP_NEW_FOLDER_ID,
                        parent: parentId,
                        name,
                    };
                    let target = s.folders;

                    if (parentId) {
                        target = s.folders.find(({ id }) => id === parentId).children;
                    }

                    let insertAt = target.length;

                    if (after) {
                        insertAt = target.findIndex(({ id }) => id === after);
                    }

                    insertOrderedItem(target, insertAt, tmpFolder);
                })
                .addCase(answersRemove.fulfilled, (s, { payload: { surveyId } }) => {
                    updateItemInArray(s.items, surveyId, (s: Survey) => {
                        s.responseCount = 0;
                        s.fetch = false;
                    });
                    s.chartData = cloneDeep(initialChartData);
                    s.charts = [];
                })
                .addCase(getPersonalPasswords.fulfilled, ({ items }, { payload: { passwords, surveyId } }) => {
                    updateItemInArray(items, surveyId, (s: Survey) => {
                        s.individualPasswords = passwords;
                    });
                })
                .addCase(getShareLinks.pending, (s) => {
                    s.shareLinksSurveyId = null;
                })
                .addCase(getShareLinks.fulfilled, (s, { payload: { shareLinks, surveyId } }) => {
                    s.shareLinks = shareLinks.map(mapShareLink);
                    s.shareLinksSurveyId = surveyId;
                })
                .addCase(addShareLink.fulfilled, (s, { payload: { shareLink } }) => {
                    s.shareLinks.push(mapShareLink(shareLink));
                })
                .addCase(changeShareLink.fulfilled, (s, { payload: { shareLink } }) => {
                    s.shareLinks = s.shareLinks.map((item) =>
                        item.id === shareLink.ID ? mapShareLink(shareLink) : item,
                    );
                })
                .addCase(deleteShareLink.fulfilled, (s, { payload: { id } }) => {
                    s.shareLinks = s.shareLinks.filter((item) => item.id !== id);
                })
                .addCase(folderAdd.fulfilled, (s, { payload: { id } }) => {
                    const folder = findFolder(s.folders, TMP_NEW_FOLDER_ID);
                    folder.id = id;
                    folder.edit = true;
                    s.fetchFolders = false;
                })
                .addCase(folderMove.pending, (s, { meta: { arg } }) => {
                    const { folders } = s;
                    const { folderId, overId } = arg;
                    const { parent } = findFolder(folders, folderId);
                    const targetFolders = parent === null ? folders : findFolder(folders, parent).children;

                    const pos = findRelativePos(targetFolders, folderId, overId);
                    arg.pos = pos;

                    if (pos !== ListPos.SAME) {
                        if (parent) {
                            s.folders = s.folders.map((item) => {
                                return item.id === parent
                                    ? { ...item, children: getOrderedMoveAndSort(targetFolders, folderId, overId) }
                                    : item;
                            });
                        } else {
                            s.folders = getOrderedMoveAndSort(targetFolders, folderId, overId);
                        }
                    }
                })
                .addCase(folderMove.fulfilled, (s) => {
                    s.fetchFolders = false;
                })
                .addCase(startResult.fulfilled, (s, action) => {
                    startPublicSuccess(s, action);
                })
                .addMatcher(
                    isAnyOf(getSurveyPromocodes.fulfilled, addPromocodes.fulfilled),
                    ({ items }, { payload: { promocodes, surveyId } }) => {
                        updateItemInArray(items, surveyId, (s: Survey) => {
                            s.promocodes = promocodes;
                            s.promocodesLoaded = true;
                        });
                    },
                )
                .addMatcher(isAnyOf(addonSave.fulfilled, saveSurvey.fulfilled), (s, { payload }) => {
                    saveFetch(s, payload);
                })
                .addMatcher(isAnyOf(deleteUserFiles.rejected, deleteUserFiles.fulfilled), (s) => {
                    s.deleteFiles = false;
                })
                .addMatcher(
                    isAnyOf(
                        getSurvey.pending,
                        licenseApply.pending,
                        removeSurvey.pending,
                        answersRemove.pending,
                        copySurvey.pending,
                        moveToFolder.pending,
                    ),
                    (s, { meta: { arg } }) => {
                        fetch(s, arg.surveyId);
                    },
                )
                .addMatcher(isAnyOf(licenseApply.rejected), (s, { meta: { arg } }) => {
                    fetchEnd(s, arg);
                })
                .addMatcher(
                    isAnyOf(
                        getSurvey.rejected,
                        addSurvey.rejected,
                        addonSave.rejected,
                        saveSurvey.rejected,
                        removeSurvey.rejected,
                        copySurvey.rejected,
                        answersRemove.rejected,
                        moveToFolder.rejected,
                    ),
                    (s, { meta: { arg } }) => {
                        fail(s, arg);
                    },
                )
                .addMatcher(
                    isAnyOf(folderListGet.pending, folderRemove.pending, folderAdd.pending, folderSave.pending),
                    (s) => {
                        folderFetch(s);
                    },
                )
                .addMatcher(isAnyOf(folderAdd.rejected, folderRemove.rejected, folderMove.rejected), (s) => {
                    folderFetchEnd(s);
                    // dispatch(folderListGet());
                })
                .addMatcher(isAnyOf(folderListGet.rejected, folderSave.rejected), (s) => {
                    folderFetchEnd(s);
                })
                .addMatcher(isAnyOf(getSurveyList.pending, addSurvey.pending), (s) => {
                    listFetch(s);
                })
                .addMatcher(
                    isAnyOf(addSurvey.fulfilled, copySurvey.fulfilled),
                    (s, { payload: { activateLicense, survey } }) => {
                        const newSurvey: Survey = {
                            ...cloneDeep(initialSurvey),
                            ...survey,
                            fetch: false,
                            expandSettings: false,
                            isPro: !activateLicense,
                        };

                        s.items.unshift(newSurvey);
                        s.activeSurveyId = newSurvey.id;
                        s.fetchItems = false;

                        countFolders(s);
                    },
                ),
        ),
});
